import type { ScienceInvestment } from '../../types/province';

/**
 * SF-161: pure-logic extraction of the "Learning the Arts" 24-day investment planner matrix,
 * ported from council/tabs/ScienceTab.tsx. Split into its own module (not inside
 * SciencesOverviewTab.tsx) so it can be unit-tested directly — a named export from a `.tsx`
 * component file is blocked by react-refresh/only-export-components (buildingsPercent.ts /
 * navPillars.ts precedent). Display-name resolution is NOT done here (Phase 3 Decision 5) — the
 * component resolves each row's label inline (see SciencesOverviewTab.tsx's `resolveLabel`).
 */

export const SCIENCE_TYPE_ORDER = ['ALCHEMY', 'TOOLS', 'HOUSING', 'FOOD', 'MILITARY', 'THIEVERY', 'CHANNELING'];

export interface InvestmentPlannerScience {
  scienceType: string;
  byDay: Record<number, number>;
}

export interface InvestmentPlannerData {
  days: number[];
  sciences: InvestmentPlannerScience[];
  totals: Record<number, number>;
}

export function buildInvestmentPlannerData(investments: ScienceInvestment[]): InvestmentPlannerData | null {
  if (!investments || investments.length === 0) return null;

  const map = new Map<string, Record<number, number>>();
  for (const inv of investments) {
    const entry = map.get(inv.scienceType) ?? {};
    entry[inv.ticksRemaining] = (entry[inv.ticksRemaining] ?? 0) + inv.bookCount;
    map.set(inv.scienceType, entry);
  }

  const days = Array.from({ length: 24 }, (_, i) => i + 1);

  const orderedTypes = SCIENCE_TYPE_ORDER.filter((st) => map.has(st));
  for (const st of map.keys()) {
    if (!orderedTypes.includes(st)) orderedTypes.push(st);
  }

  const sciences = orderedTypes.map((st) => ({
    scienceType: st,
    byDay: map.get(st)!,
  }));

  const totals: Record<number, number> = {};
  for (const day of days) {
    totals[day] = investments
      .filter((inv) => inv.ticksRemaining === day)
      .reduce((sum, inv) => sum + inv.bookCount, 0);
  }

  return { days, sciences, totals };
}
